import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getPublicOpportunities, createGuestRsvp, confirmGuestRsvp } from '../services/apiService';
import MapPinIcon from './icons/MapPinIcon';
import CalendarIcon from './icons/CalendarIcon';
import UsersIcon from './icons/UsersIcon';
import CloseIcon from './icons/CloseIcon';

interface PublicOpportunity {
    id: string;
    title: string;
    description: string;
    type: string;
    location: string;
    startDate?: string;
    endDate?: string;
    startTime?: string;
    imageUrl?: string;
    maxCapacity?: number;
    attendeeCount?: number;
    author: { name: string; handle: string; avatarUrl: string };
    timestamp: string;
}

const TYPE_COLORS: Record<string, string> = {
    'Gig':             'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Event':           'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Live Show':       'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Stage Play':      'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'Open Mic':        'bg-green-500/20 text-green-400 border-green-500/30',
    'DJ set':          'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'Stand Up Comedy': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Showcase':        'bg-red-500/20 text-red-400 border-red-500/30',
};

const formatDate = (startDate?: string, endDate?: string): string | null => {
    if (!startDate) return null;
    const start = new Date(`${startDate}T00:00:00`);
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    if (!endDate || endDate === startDate) return start.toLocaleDateString(undefined, opts);
    const end = new Date(`${endDate}T00:00:00`);
    return `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, opts)}`;
};

// ── Share buttons ────────────────────────────────────────────────────────────

const ShareBar: React.FC<{ event: PublicOpportunity }> = ({ event }) => {
    const [copied, setCopied] = useState(false);
    const url = `${window.location.origin}/?id=${event.id}`;
    const text = encodeURIComponent(`${event.title} — ${event.location}`);
    const encodedUrl = encodeURIComponent(url);

    const copy = () => {
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="mt-4">
            <p className="text-[10px] font-black text-brand-muted uppercase tracking-widest mb-2">Share</p>
            <div className="flex items-center gap-2 flex-wrap">
                <button onClick={copy} className="flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full border border-gray-700 text-gray-400 hover:border-brand-orange hover:text-brand-orange transition-all uppercase tracking-widest">
                    {copied ? '✓ Copied!' : '🔗 Copy link'}
                </button>
                <a href={`https://wa.me/?text=${text}%20${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] font-black px-3 py-1.5 rounded-full border border-gray-700 text-gray-400 hover:border-green-500 hover:text-green-400 transition-all uppercase tracking-widest">
                    WhatsApp
                </a>
                <a href={`https://x.com/intent/tweet?text=${text}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] font-black px-3 py-1.5 rounded-full border border-gray-700 text-gray-400 hover:border-sky-400 hover:text-sky-400 transition-all uppercase tracking-widest">
                    X
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer"
                    className="text-[10px] font-black px-3 py-1.5 rounded-full border border-gray-700 text-gray-400 hover:border-blue-500 hover:text-blue-400 transition-all uppercase tracking-widest">
                    Facebook
                </a>
            </div>
        </div>
    );
};

// ── Guest RSVP form ──────────────────────────────────────────────────────────

type RsvpStep = 'form' | 'code' | 'done';

const GuestRsvpForm: React.FC<{ event: PublicOpportunity; onJoin: () => void }> = ({ event, onJoin }) => {
    const [step, setStep] = useState<RsvpStep>('form');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName]   = useState('');
    const [email, setEmail]         = useState('');
    const [code, setCode]           = useState('');
    const [rsvpId, setRsvpId]       = useState('');
    const [error, setError]         = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isFull = event.maxCapacity != null && (event.attendeeCount ?? 0) >= event.maxCapacity;

    const handleSubmitForm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const res = await createGuestRsvp(event.id, { firstName, lastName, email });
            setRsvpId(res.rsvpId);
            setStep('code');
        } catch (err: any) {
            if (err.message === 'ALREADY_CONFIRMED') setError('You already have a confirmed spot for this event.');
            else if (err.message === 'EVENT_FULL') setError('Sorry, this event is now full.');
            else setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            await confirmGuestRsvp(event.id, rsvpId, code);
            setStep('done');
        } catch (err: any) {
            if (err.message === 'INVALID_CODE') setError('Invalid code. Please check and try again.');
            else if (err.message === 'CODE_EXPIRED') setError('Code expired. Please start over.');
            else setError(err.message || 'Something went wrong.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isFull) return (
        <p className="text-center text-red-400 font-black text-sm uppercase tracking-widest py-4">This event is full</p>
    );

    if (step === 'done') return (
        <div className="text-center py-6 space-y-3">
            <div className="text-5xl">🎉</div>
            <p className="text-white font-black text-lg uppercase tracking-tight">You're in!</p>
            <p className="text-gray-400 text-sm">Your spot at <strong>{event.title}</strong> is confirmed.</p>
            <p className="text-gray-500 text-xs mt-2">Want to do more? Create your free beYourAMP profile.</p>
            <button onClick={onJoin} className="mt-2 bg-brand-orange text-white font-black px-6 py-2.5 rounded-full hover:bg-orange-600 transition-all text-xs uppercase tracking-widest">
                Create Profile →
            </button>
        </div>
    );

    if (step === 'code') return (
        <form onSubmit={handleConfirmCode} className="space-y-4">
            <div className="text-center mb-2">
                <p className="text-white font-bold">Check your email</p>
                <p className="text-gray-400 text-sm">We sent a 6-digit code to <strong>{email}</strong></p>
            </div>
            <input
                type="text" inputMode="numeric" maxLength={6} value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full bg-gray-800 text-white text-center text-3xl font-black tracking-[0.5em] rounded-xl p-4 border border-gray-700 focus:outline-none focus:border-brand-orange"
                required
            />
            {error && <p className="text-red-400 text-xs text-center">{error}</p>}
            <button type="submit" disabled={isLoading || code.length < 6}
                className="w-full bg-brand-orange text-white font-black py-3 rounded-full hover:bg-orange-600 transition-all disabled:opacity-50 uppercase tracking-widest text-sm">
                {isLoading ? 'Confirming…' : 'Confirm Attendance'}
            </button>
            <button type="button" onClick={() => { setStep('form'); setCode(''); setError(''); }}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors">
                ← Back
            </button>
        </form>
    );

    return (
        <form onSubmit={handleSubmitForm} className="space-y-3">
            <p className="text-white font-bold text-sm mb-1">Reserve your spot</p>
            <div className="grid grid-cols-2 gap-3">
                <input value={firstName} onChange={e => setFirstName(e.target.value)}
                    placeholder="First name" required
                    className="bg-gray-800 text-white rounded-lg p-3 text-sm border border-gray-700 focus:outline-none focus:border-brand-orange" />
                <input value={lastName} onChange={e => setLastName(e.target.value)}
                    placeholder="Last name" required
                    className="bg-gray-800 text-white rounded-lg p-3 text-sm border border-gray-700 focus:outline-none focus:border-brand-orange" />
            </div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Email address" required
                className="w-full bg-gray-800 text-white rounded-lg p-3 text-sm border border-gray-700 focus:outline-none focus:border-brand-orange" />
            {error && <p className="text-red-400 text-xs">{error}</p>}
            <button type="submit" disabled={isLoading}
                className="w-full bg-brand-orange text-white font-black py-3 rounded-full hover:bg-orange-600 transition-all disabled:opacity-50 uppercase tracking-widest text-sm">
                {isLoading ? 'Sending code…' : 'Send Confirmation Code'}
            </button>
            <p className="text-[10px] text-gray-500 text-center">We'll send a 6-digit code to confirm your spot.</p>
        </form>
    );
};

// ── Event detail modal ───────────────────────────────────────────────────────

const EventModal: React.FC<{ event: PublicOpportunity; onClose: () => void; onJoin: () => void }> = ({ event, onClose, onJoin }) => {
    const date = formatDate(event.startDate, event.endDate);
    const typeColor = TYPE_COLORS[event.type] || 'bg-gray-700/50 text-gray-400 border-gray-600';
    const hasCapacity = event.maxCapacity != null;
    const isFull = hasCapacity && (event.attendeeCount ?? 0) >= event.maxCapacity!;
    const fillPct = hasCapacity ? Math.min(((event.attendeeCount ?? 0) / event.maxCapacity!) * 100, 100) : 0;

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-[#1E1E1E] w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-gray-700 shadow-2xl overflow-y-auto max-h-[92vh] sm:max-h-[85vh]">

                {/* Header */}
                <div className="sticky top-0 bg-[#1E1E1E] border-b border-gray-800 flex items-center justify-between px-5 py-4 z-10">
                    <span className={`text-[10px] font-black px-3 py-1 rounded-full border uppercase tracking-widest ${typeColor}`}>
                        {event.type}
                    </span>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
                        <CloseIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Cover image */}
                {event.imageUrl && (
                    <img src={event.imageUrl} alt={event.title} className="w-full h-52 object-cover" />
                )}

                <div className="p-5 space-y-4">

                    {/* Title + author */}
                    <div>
                        <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight">{event.title}</h2>
                        <div className="flex items-center gap-2 mt-2">
                            <img src={event.author.avatarUrl} alt={event.author.name} className="h-6 w-6 rounded-full object-cover border border-gray-700" />
                            <span className="text-xs text-gray-400">{event.author.name}</span>
                        </div>
                    </div>

                    {/* Meta pills */}
                    <div className="flex flex-wrap gap-3 text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                        <span className="flex items-center gap-1.5">
                            <MapPinIcon className="h-3.5 w-3.5 text-brand-orange flex-shrink-0" />
                            {event.location}
                        </span>
                        {date && (
                            <span className="flex items-center gap-1.5">
                                <CalendarIcon className="h-3.5 w-3.5 text-brand-orange flex-shrink-0" />
                                {date}{event.startTime ? ` · ${event.startTime}` : ''}
                            </span>
                        )}
                    </div>

                    {/* Capacity bar */}
                    {hasCapacity && (
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-bold">
                                <span className="flex items-center gap-1 text-gray-400">
                                    <UsersIcon className="h-3.5 w-3.5" />
                                    {event.attendeeCount ?? 0} / {event.maxCapacity} spots taken
                                </span>
                                {isFull && <span className="text-red-400 uppercase tracking-widest text-[10px]">Full</span>}
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                <div className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-500' : fillPct > 75 ? 'bg-yellow-500' : 'bg-brand-orange'}`}
                                    style={{ width: `${fillPct}%` }} />
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    <p className="text-sm text-gray-300 leading-relaxed">{event.description}</p>

                    {/* Share */}
                    <ShareBar event={event} />

                    {/* CTA / RSVP */}
                    <div className="pt-2 border-t border-gray-800">
                        {hasCapacity ? (
                            <GuestRsvpForm event={event} onJoin={onJoin} />
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3">
                                    <span className="text-green-400 text-lg">✓</span>
                                    <p className="text-green-300 text-sm font-bold">Open Event — free admission, no reservation needed</p>
                                </div>
                                <div className="pt-1 border-t border-gray-800/50 text-center">
                                    <p className="text-gray-500 text-xs mb-2">Want to interact, comment and follow this event?</p>
                                    <button onClick={onJoin}
                                        className="w-full bg-brand-orange text-white font-black py-2.5 rounded-full hover:bg-orange-600 transition-all uppercase tracking-widest text-xs shadow-lg shadow-brand-orange/20">
                                        Create Free Profile →
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── Carousel card (Netflix style) ───────────────────────────────────────────

const CarouselCard: React.FC<{ event: PublicOpportunity; onSelect: () => void }> = ({ event, onSelect }) => {
    const date = formatDate(event.startDate, event.endDate);
    const typeColor = TYPE_COLORS[event.type] || 'bg-gray-700/50 text-gray-400 border-gray-600';
    const hasCapacity = event.maxCapacity != null;
    const isFull = hasCapacity && (event.attendeeCount ?? 0) >= event.maxCapacity!;

    return (
        <div
            onClick={onSelect}
            className="relative flex-shrink-0 w-56 sm:w-64 h-80 rounded-xl overflow-hidden cursor-pointer group border border-gray-800 hover:border-gray-500 transition-all hover:scale-[1.03] hover:z-10 hover:shadow-2xl"
        >
            {/* Background image */}
            {event.imageUrl ? (
                <img src={event.imageUrl} alt={event.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900" />
            )}

            {/* Always-on gradient + title at bottom */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-3">
                <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border uppercase tracking-widest ${typeColor}`}>
                    {event.type}
                </span>
                <h3 className="mt-1.5 text-sm font-black text-white uppercase tracking-tight leading-snug line-clamp-2">
                    {event.title}
                </h3>
            </div>

            {/* Hover overlay with details */}
            <div className="absolute inset-0 bg-black/85 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-4">
                <div className="space-y-2">
                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border uppercase tracking-widest ${typeColor}`}>
                        {event.type}
                    </span>
                    <h3 className="text-sm font-black text-white uppercase tracking-tight leading-snug line-clamp-2">
                        {event.title}
                    </h3>
                    <div className="flex flex-col gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                        {date && (
                            <span className="flex items-center gap-1">
                                <CalendarIcon className="h-3 w-3 text-brand-orange flex-shrink-0" />
                                {date}{event.startTime ? ` · ${event.startTime}` : ''}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <MapPinIcon className="h-3 w-3 text-brand-orange flex-shrink-0" />
                            <span className="line-clamp-1">{event.location}</span>
                        </span>
                        {hasCapacity && (
                            <span className={`flex items-center gap-1 ${isFull ? 'text-red-400' : ''}`}>
                                <UsersIcon className="h-3 w-3 flex-shrink-0" />
                                {isFull ? 'FULL' : `${event.attendeeCount ?? 0} / ${event.maxCapacity} spots`}
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-400 leading-relaxed line-clamp-3">{event.description}</p>
                </div>
                <button
                    className={`w-full py-2 rounded-full font-black text-[11px] uppercase tracking-widest transition-all mt-2 ${
                        isFull
                            ? 'bg-gray-700 text-gray-500 cursor-default'
                            : hasCapacity
                                ? 'bg-brand-orange text-white hover:bg-orange-600'
                                : 'border border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white'
                    }`}
                    disabled={isFull}
                >
                    {isFull ? 'Event Full' : hasCapacity ? 'Reserve Spot →' : 'Open Event →'}
                </button>
            </div>
        </div>
    );
};

// ── Carousel row ─────────────────────────────────────────────────────────────

const CarouselRow: React.FC<{
    title: string;
    events: PublicOpportunity[];
    onSelect: (e: PublicOpportunity) => void;
}> = ({ title, events, onSelect }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft]   = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const updateArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 8);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
    };

    const scroll = (dir: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({ left: dir === 'left' ? -500 : 500, behavior: 'smooth' });
    };

    return (
        <div className="mb-10">
            <h2 className="px-4 sm:px-6 mb-3 text-base font-black text-white uppercase tracking-widest">
                {title}
            </h2>
            <div className="relative group/row">
                {/* Left arrow */}
                {canScrollLeft && (
                    <button
                        onClick={() => scroll('left')}
                        className="hidden sm:flex absolute left-0 top-0 bottom-0 z-10 items-center justify-center w-10 bg-gradient-to-r from-[#121212] to-transparent text-white opacity-0 group-hover/row:opacity-100 transition-opacity"
                    >
                        <span className="text-xl font-black">‹</span>
                    </button>
                )}

                {/* Scroll container */}
                <div
                    ref={scrollRef}
                    onScroll={updateArrows}
                    className="flex gap-3 overflow-x-auto px-4 sm:px-6 pb-2 scrollbar-hide scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {events.map(event => (
                        <CarouselCard key={event.id} event={event} onSelect={() => onSelect(event)} />
                    ))}
                </div>

                {/* Right arrow */}
                {canScrollRight && (
                    <button
                        onClick={() => scroll('right')}
                        className="hidden sm:flex absolute right-0 top-0 bottom-0 z-10 items-center justify-center w-10 bg-gradient-to-l from-[#121212] to-transparent text-white opacity-0 group-hover/row:opacity-100 transition-opacity"
                    >
                        <span className="text-xl font-black">›</span>
                    </button>
                )}
            </div>
        </div>
    );
};

// ── Main landing page ────────────────────────────────────────────────────────

const CAROUSEL_TYPES = ['Gig', 'Event', 'Live Show', 'DJ set', 'Stage Play', 'Open Mic', 'Stand Up Comedy', 'Showcase', 'Workshop', 'Presentation', 'Vernissage', 'Collective Exhibition'];

interface PublicLandingPageProps { onShowAuth: () => void; }

const PublicLandingPage: React.FC<PublicLandingPageProps> = ({ onShowAuth }) => {
    const [events, setEvents]               = useState<PublicOpportunity[]>([]);
    const [isLoading, setIsLoading]         = useState(true);
    const [lastDocId, setLastDocId]         = useState<string | null>(null);
    const [hasMore, setHasMore]             = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<PublicOpportunity | null>(null);

    useEffect(() => {
        getPublicOpportunities()
            .then(data => { setEvents(data.opportunities); setLastDocId(data.lastDocId); setHasMore(data.hasMore); })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    }, []);

    const loadMore = useCallback(async () => {
        if (!lastDocId || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const data = await getPublicOpportunities(lastDocId);
            setEvents(prev => [...prev, ...data.opportunities]);
            setLastDocId(data.lastDocId);
            setHasMore(data.hasMore);
        } catch (e) { console.error(e); }
        finally { setIsLoadingMore(false); }
    }, [lastDocId, isLoadingMore]);

    // Group events by type — only types with at least 1 event
    const byType = useMemo(() => {
        const map: Record<string, PublicOpportunity[]> = {};
        for (const e of events) {
            if (!map[e.type]) map[e.type] = [];
            map[e.type].push(e);
        }
        return map;
    }, [events]);

    const activeTypes = useMemo(() =>
        CAROUSEL_TYPES.filter(t => (byType[t]?.length ?? 0) >= 1),
        [byType]
    );

    const skeletonRow = (
        <div className="flex gap-3 px-4 sm:px-6 mb-10">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-56 sm:w-64 h-80 rounded-xl bg-[#1E1E1E] border border-gray-800 animate-pulse" />
            ))}
        </div>
    );

    return (
        <div className="min-h-screen bg-[#121212] font-urbanist">

            {/* ── Header ── */}
            <header className="sticky top-0 z-50 bg-[#121212]/90 backdrop-blur border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <img src="/logo.png" alt="beYourAMP" className="h-10 w-auto" />
                    <div className="flex items-center gap-3">
                        <button onClick={onShowAuth} className="text-sm font-bold text-gray-300 hover:text-white transition-colors">
                            Login
                        </button>
                        <button onClick={onShowAuth} className="text-sm font-black bg-brand-orange text-white px-5 py-2 rounded-full hover:bg-orange-600 transition-all shadow-lg shadow-brand-orange/20 tracking-widest uppercase">
                            Join Free
                        </button>
                    </div>
                </div>
            </header>

            {/* ── Hero ── */}
            <section className="relative overflow-hidden pt-14 pb-16 text-center">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?q=80&w=2070&auto=format&fit=crop"
                        className="w-full h-full object-cover opacity-40"
                        alt=""
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/60 via-transparent to-[#121212]" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="flex justify-center mb-6">
                        <img src="/logo.png" alt="beYourAMP" className="h-24 sm:h-32 w-auto" />
                    </div>
                    <h1 className="text-4xl sm:text-6xl font-black text-white uppercase tracking-tighter leading-none mb-4">
                        Discover Events<br /><span className="text-brand-orange">Near You</span>
                    </h1>
                    <p className="text-gray-300 text-lg max-w-xl mx-auto mb-6 font-medium">
                        Gigs, live shows, DJ sets, theatre, standup and more.
                    </p>
                    <button onClick={onShowAuth} className="bg-brand-orange text-white font-black px-8 py-3.5 rounded-full hover:bg-orange-600 transition-all shadow-xl shadow-brand-orange/30 tracking-widest uppercase text-sm">
                        Join &amp; Attend →
                    </button>
                </div>
            </section>

            {/* ── Carousels ── */}
            <main className="pb-20">
                {isLoading ? (
                    <>
                        <div className="px-4 sm:px-6 mb-3 h-5 w-32 bg-gray-800 rounded animate-pulse" />
                        {skeletonRow}
                        <div className="px-4 sm:px-6 mb-3 h-5 w-24 bg-gray-800 rounded animate-pulse" />
                        {skeletonRow}
                    </>
                ) : events.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-gray-500 text-lg font-medium">No events yet</p>
                        <p className="text-gray-600 text-sm mt-1">Check back soon or join to post your own</p>
                    </div>
                ) : (
                    <>
                        {/* All events carousel */}
                        <CarouselRow
                            title="All Events"
                            events={events}
                            onSelect={setSelectedEvent}
                        />

                        {/* Per-type carousels */}
                        {activeTypes.map(type => (
                            <CarouselRow
                                key={type}
                                title={type}
                                events={byType[type]}
                                onSelect={setSelectedEvent}
                            />
                        ))}

                        {hasMore && (
                            <div className="mt-4 text-center px-4">
                                <button onClick={loadMore} disabled={isLoadingMore}
                                    className="bg-gray-800 text-white font-bold px-8 py-3 rounded-full hover:bg-gray-700 transition-colors disabled:opacity-50 tracking-widest uppercase text-xs">
                                    {isLoadingMore ? 'Loading…' : 'Load More Events'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* ── Bottom CTA ── */}
            <section className="border-t border-gray-800 bg-[#1E1E1E]">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-3">
                        Artist, venue or service?
                    </h2>
                    <p className="text-gray-400 mb-6 font-medium">Post events, find collaborators, connect with fans.</p>
                    <button onClick={onShowAuth} className="bg-brand-orange text-white font-black px-10 py-4 rounded-full hover:bg-orange-600 transition-all shadow-xl shadow-brand-orange/30 tracking-widest uppercase text-sm">
                        Create Your Profile →
                    </button>
                </div>
            </section>

            {/* ── Event detail modal ── */}
            {selectedEvent && (
                <EventModal
                    event={selectedEvent}
                    onClose={() => setSelectedEvent(null)}
                    onJoin={onShowAuth}
                />
            )}
        </div>
    );
};

export default PublicLandingPage;
